import type { HTMLAttributes } from 'react';

type Variant = 'certified' | 'exempt' | 'pending' | 'fail';
type Size = 'sm' | 'md';

interface IPPCStampProps extends HTMLAttributes<HTMLSpanElement> {
  /** 徽标文字。可覆盖默认显示，如 "HT"、"DB"、"MB"、"免熏蒸" */
  label?: string;
  variant?: Variant;
  /** 口岸代码，默认 CN-31（上海）*/
  portCode?: string;
  size?: Size;
  /** 紧凑模式：不显示 "IPPC · CN-31 · " 前缀，只显示状态字（适合密集表格）*/
  compact?: boolean;
}

// 仿 ISPM-15 真实蓝章样式 · 2px 方框 + 等宽大写 + 行业蓝
const variantClasses: Record<Variant, string> = {
  certified: 'border-ippc-500 bg-ippc-100 text-ippc-700',
  exempt:    'border-success bg-success-soft text-[#3a5e3d]',
  pending:   'border-warning bg-warning-soft text-[#8a5a1f]',
  fail:      'border-danger bg-danger-soft text-danger',
};

const sizeClasses: Record<Size, { box: string; dot: string; text: string }> = {
  sm: { box: 'px-1.5 py-[1px] gap-1',  dot: 'w-3 h-3 text-[7px]',  text: 'text-[10px]' },
  md: { box: 'px-2.5 py-[2px] gap-1.5', dot: 'w-4 h-4 text-[9px]',  text: 'text-[11px]' },
};

const defaultLabel: Record<Variant, string> = {
  certified: 'HT',
  exempt:    '免熏蒸',
  pending:   '待熏蒸',
  fail:      '熏蒸失败',
};

/**
 * 签名动作 D · IPPC 检疫章徽标
 *
 * 仿 ISPM-15 国际木包装标准的真实蓝色熏蒸合格章样式。
 * 出口木包装行业每天看到的视觉符号，行业内识别度极高，
 * 任何竞品 ERP 都未使用。详见 DESIGN.md §9.4。
 *
 * variant 语义：
 *   certified — 已完成热处理（HT）/ 除皮（DB）/ 溴甲烷熏蒸（MB），出 IPPC 证书
 *   exempt    — 胶合板/LVL 免熏蒸
 *   pending   — 待熏蒸入炉
 *   fail      — 熏蒸不合格（温度未达/时长不足）
 *
 * @example
 *   <IPPCStamp variant="certified" label="HT" />
 *   <IPPCStamp variant="certified" portCode="CN-31" label="DB" />
 *   <IPPCStamp variant="exempt" />
 *   <IPPCStamp variant="pending" />
 */
export default function IPPCStamp({
  label,
  variant = 'certified',
  portCode = 'CN-31',
  size = 'md',
  compact = false,
  className = '',
  ...rest
}: IPPCStampProps) {
  const text = label ?? defaultLabel[variant];
  const s = sizeClasses[size];
  // exempt / pending 类不挂 IPPC 口岸号（它们不是真证书），只显示徽标+状态文字
  // compact 模式：在密集表格里隐藏前缀，只看 HT/DB/MB
  const showPortCode = variant === 'certified' && !compact;

  return (
    <span
      {...rest}
      className={`inline-flex items-center border-2 font-mono font-semibold uppercase ${variantClasses[variant]} ${s.box} ${s.text} ${className}`}
      style={{ letterSpacing: '0.08em', borderRadius: '2px' }}
      title={
        variant === 'certified'
          ? `IPPC · ${portCode} · ${text}`
          : variant === 'exempt'
          ? '免熏蒸产品（胶合板/LVL）'
          : variant === 'pending'
          ? '待熏蒸入炉'
          : '熏蒸不合格'
      }
    >
      <span
        className={`inline-grid place-items-center rounded-full border-[1.5px] ${s.dot}`}
        style={{ borderColor: 'currentColor' }}
        aria-hidden
      >
        ◎
      </span>
      <span>
        {showPortCode ? (
          <>
            <span>IPPC · {portCode} · </span>
            <span>{text}</span>
          </>
        ) : (
          <span>{text}</span>
        )}
      </span>
    </span>
  );
}
